// client/src/components/secure/AddListingPaste.js — "Add a listing" by paste or bookmarklet.
//
// LoopNet, Crexi and CityFeet forbid automated access, so the portal never visits a
// listing page. The owner brings the page here himself: the URL and/or the page text
// (select all, copy, paste), or the Send to BREC bookmarklet, which hands over the same
// three things. The server reads what it can (and, when a Claude key is set, the AI can
// read it too); the result is ALWAYS shown in this editable form before anything is saved.
import React, { useState, useEffect, useRef } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import DataList from '../../components/shared/DataList';

const SOURCE_LABEL = {
    loopnet: 'LoopNet', crexi: 'Crexi', cityfeet: 'CityFeet', commercialcafe: 'CommercialCafe',
    commercialsearch: 'CommercialSearch', zillow: 'Zillow', realtor: 'Realtor.com', apartments: 'Apartments.com', forrent: 'ForRent', redfin: 'Redfin', trulia: 'Trulia', homes: 'Homes.com', other: 'Other',
};
const BLOCKED = ['loopnet.com', 'crexi.com', 'cityfeet.com'];
const sourceOf = (url) => {
    let host = '';
    try { host = new URL(String(url).trim()).hostname.toLowerCase(); } catch { return url ? 'other' : null; }
    const hit = Object.keys(SOURCE_LABEL).find((k) => k !== 'other' && (host === `${k}.com` || host.endsWith(`.${k}.com`)));
    return hit || 'other';
};
const isBlocked = (url) => {
    try {
        const h = new URL(String(url).trim()).hostname.toLowerCase();
        return BLOCKED.some((d) => h === d || h.endsWith(`.${d}`));
    } catch { return false; }
};

const EMPTY = {
    address: '', municipality: '', size_sf: '', rate: '', listing_type: '', property_type: '',
    listing_agent: '', listing_brokerage: '', agent_phone: '', agent_email: '', listing_zoning: '', notes: '',
};
const FIELD_LABELS = [
    ['address', 'Address', '1945 Techny Rd'], ['municipality', 'Town', 'Northbrook'],
    ['size_sf', 'Size SF (each space)', '2,400; 4,531'], ['rate', 'Rate / price', '$12.50/SF/yr NNN'],
    ['property_type', 'Property type', 'Office / Flex'],
    ['listing_agent', 'Listing agent', ''], ['listing_brokerage', 'Brokerage', ''],
    ['agent_phone', 'Agent phone', ''], ['agent_email', 'Agent email', ''],
    ['listing_zoning', 'Zoning the listing states', 'I-1'],
];

const fromParsed = (fields = {}) => {
    const out = { ...EMPTY };
    for (const k of Object.keys(EMPTY)) if (fields[k]) out[k] = String(fields[k]);
    return out;
};

// ---------------------------------------------------------------------------
// The bookmarklet. Built here, with this portal's address and the user's clip key.
// ---------------------------------------------------------------------------
const bookmarkletCode = (origin, key) => `(function(){
var P=${JSON.stringify(origin)},K=${JSON.stringify(key)},M=60000,s='';
try{s=String(window.getSelection()||'')}catch(e){}
var sel=s.replace(/\\s/g,'').length>40;
var t=(sel?s:((document.body&&document.body.innerText)||'')).slice(0,M);
var f=document.createElement('form');f.method='POST';f.action=P+'/api/site-search-clip';f.target='_blank';f.acceptCharset='UTF-8';f.style.display='none';
var d={k:K,u:location.href.slice(0,2000),h:(document.title||'').slice(0,300),x:t,s:sel?'1':''};
for(var n in d){var i=document.createElement('textarea');i.name=n;i.value=d[n];f.appendChild(i);}
var done=false;
function fb(e){var v=String((e&&(e.violatedDirective||e.effectiveDirective))||'');if(done||v.indexOf('form-action')<0)return;done=true;
var w=window.open(P+'/dashboard/site-search-clip#u='+encodeURIComponent(location.href.slice(0,1500))+'&h='+encodeURIComponent((document.title||'').slice(0,200)),'_blank');
if(!w)alert('This site blocked Send to BREC. Copy the page (Ctrl+A, Ctrl+C) and paste it into Add a listing in the portal.');}
document.addEventListener('securitypolicyviolation',fb);
(document.body||document.documentElement).appendChild(f);f.submit();
setTimeout(function(){f.remove();document.removeEventListener('securitypolicyviolation',fb);},3000);
})();`;

// The key is stored only hashed on the server, so it is handed over once (first use, or
// after a reset) and kept in this browser to draw the button again.
const KEY_STORE = (userId) => `brec.sendToBrec.${userId || 'me'}`;
const readKey = (userId) => { try { return window.localStorage.getItem(KEY_STORE(userId)) || null; } catch { return null; } };
const writeKey = (userId, key) => { try { window.localStorage.setItem(KEY_STORE(userId), key); } catch { /* storage off */ } };
const dropKey = (userId) => { try { window.localStorage.removeItem(KEY_STORE(userId)); } catch { /* storage off */ } };
const when = (d) => (d ? new Date(d).toLocaleString() : '—');

// Administrator: everyone's Send to BREC button, and a Revoke for each.
const ClipKeyAdmin = ({ apiClient }) => {
    const [rows, setRows] = useState(null);
    const [msg, setMsg] = useState('');
    const load = React.useCallback(() => {
        apiClient.get('/api/site-searches/paste/clip-keys').then(setRows).catch((e) => setMsg(e.message || 'Could not load.'));
    }, [apiClient]);
    useEffect(() => { load(); }, [load]);
    const revoke = async (r) => {
        if (!window.confirm(`Turn off ${r.name}'s Send to BREC button? Their bookmark stops working; they can make a new one with Reset.`)) return;
        try { const x = await apiClient.delete(`/api/site-searches/paste/clip-keys/${r.user_id}`); setMsg(x.message); load(); }
        catch (e) { setMsg(e.message || 'Could not revoke.'); }
    };
    return (
        <details className="mt-2">
            <summary className="cursor-pointer font-semibold">Everyone's Send to BREC buttons (administrator)</summary>
            {msg && <p className="mt-1">{msg}</p>}
            {!rows ? <p className="mt-1">Loading…</p> : rows.length === 0 ? <p className="mt-1">Nobody has one yet.</p> : (
                <div className="mt-1"><DataList rows={rows} rowKey="user_id" csvName="paste-tokens" defaultSort={{ key: 'created_at', dir: 'desc' }}
                    columns={[
                        { key: 'name', label: 'User' },
                        { key: 'created_at', label: 'Made', type: 'date', render: (r) => when(r.created_at) },
                        { key: 'last_used_at', label: 'Last used', type: 'date', render: (r) => when(r.last_used_at) },
                        { key: 'state', label: 'State', value: (r) => (r.active ? 'active' : `revoked ${when(r.revoked_at)}`), sortValue: (r) => (r.active ? 0 : 1),
                            render: (r) => (r.active
                                ? <button type="button" onClick={() => revoke(r)} className="rounded border border-red-300 px-2 py-0.5 text-red-700">Revoke</button>
                                : <span className="text-gray-500">revoked {when(r.revoked_at)}</span>) },
                    ]} /></div>
            )}
        </details>
    );
};

export const SendToBrecBookmarklet = ({ status, apiClient }) => {
    const ref = useRef(null);
    const userId = status?.user_id;
    const [clipKey, setClipKey] = useState(null);
    const [msg, setMsg] = useState('');
    const [busy, setBusy] = useState(false);
    const state = status?.clip_key_state || {};
    useEffect(() => {
        if (!status) return;
        if (status.clip_key) { writeKey(userId, status.clip_key); setClipKey(status.clip_key); }
        else if (state.has_key) setClipKey(readKey(userId));
        else { dropKey(userId); setClipKey(null); }
    }, [status, userId, state.has_key]);
    useEffect(() => {
        // React refuses javascript: URLs in JSX, so the href is set on the element itself.
        if (ref.current && clipKey) {
            const code = bookmarkletCode(window.location.origin, clipKey).replace(/\n/g, '');
            ref.current.setAttribute('href', `javascript:${encodeURIComponent(code)}`);
        }
    }, [clipKey]);
    if (!status || !state.available) return null;
    const reset = async () => {
        if (clipKey && !window.confirm('Make a new Send to BREC button? The one already on your bookmarks bar (on every browser) stops working.')) return;
        setBusy(true); setMsg('');
        try {
            const r = await apiClient.post('/api/site-searches/paste/clip-key/reset', {});
            writeKey(userId, r.clip_key); setClipKey(r.clip_key); setMsg(r.message);
        } catch (e) { setMsg(e.message || 'Could not reset.'); }
        setBusy(false);
    };
    return (
        <div className="rounded border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-900">
            <p className="font-semibold">Send to BREC — a one-click clipper for your own browser</p>
            {clipKey ? (
                <ol className="mt-1 list-decimal space-y-0.5 pl-5">
                    <li>Show your bookmarks bar (Ctrl+Shift+B in Chrome and Edge).</li>
                    <li>Drag this button onto the bar:{' '}
                        <a ref={ref} href="#send-to-brec" onClick={(e) => { e.preventDefault(); alert('Drag this button to your bookmarks bar, then click it while you are on a listing page.'); }}
                           className="ml-1 inline-block cursor-move rounded bg-slate-800 px-2 py-1 font-semibold text-white no-underline">
                            Send to BREC
                        </a>
                    </li>
                    <li>On a LoopNet, Crexi, CityFeet or any listing page, click <b>Send to BREC</b>. A portal tab opens with the
                        page's address, title and text filled in. Select part of the page first to send only that part.</li>
                    <li>Check the fields, pick the search, and save.</li>
                </ol>
            ) : (
                <p className="mt-1">
                    {state.revoked_at
                        ? `Your Send to BREC button was turned off on ${when(state.revoked_at)}. Click Reset to make a new one.`
                        : 'Your Send to BREC button was made on another browser, so it cannot be shown here (the portal keeps only a scrambled copy of its key). '
                          + 'If the button on your bookmarks bar still works, keep using it. To put one on this browser, click Reset — the old one then stops working.'}
                </p>
            )}
            <p className="mt-1 text-indigo-800">It sends only the page you are looking at, when you click it. The portal never visits
                those sites itself. The button carries your personal key — don't share it. If it is lost or shared, click Reset and
                the old one stops working. Pages wait 30 minutes; if a site blocks it, a tab still opens with the address and you paste the text.
                {state.last_used_at ? ` Last used ${when(state.last_used_at)}.` : ''}</p>
            <div className="mt-2 flex items-center gap-2">
                <button type="button" onClick={reset} disabled={busy} className="rounded border border-indigo-400 bg-white px-2 py-1 font-semibold text-indigo-800 disabled:opacity-50">
                    {busy ? 'Working…' : 'Reset my Send to BREC button'}
                </button>
                {msg && <span>{msg}</span>}
            </div>
            {status.can_manage_clip_keys && <ClipKeyAdmin apiClient={apiClient} />}
        </div>
    );
};

// ---------------------------------------------------------------------------
// The paste box + editable form
// ---------------------------------------------------------------------------
const AddListingPaste = ({ searchId, searches = null, initial = null, clipId = null, onDone, onCancel }) => {
    const apiClient = useApiClient();
    const [status, setStatus] = useState(null);
    const [url, setUrl] = useState(initial?.url || '');
    const [title, setTitle] = useState(initial?.title || '');
    const [text, setText] = useState(initial?.text || '');
    const [form, setForm] = useState(initial?.parsed ? fromParsed(initial.parsed.fields) : null);
    const [missing, setMissing] = useState(initial?.parsed?.missing || []);
    const [readBy, setReadBy] = useState(initial?.parsed ? 'paste' : null);
    const [source, setSource] = useState('other');
    const [target, setTarget] = useState(searchId || '');
    const [busy, setBusy] = useState('');
    const [error, setError] = useState('');
    const [saved, setSaved] = useState(null);

    useEffect(() => {
        let live = true;
        apiClient.get('/api/site-searches/paste/status').then((r) => { if (live) setStatus(r); }).catch(() => {});
        return () => { live = false; };
    }, [apiClient]);
    useEffect(() => { if (searchId) setTarget(searchId); }, [searchId]);

    const detected = url.trim() ? sourceOf(url) : null;
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const read = async (ai) => {
        setError(''); setBusy(ai ? 'ai' : 'parse');
        try {
            const r = await apiClient.post(`/api/site-searches/paste/${ai ? 'ai-extract' : 'parse'}`, { url, title, text });
            const next = fromParsed(r.fields);
            // An AI pass fills gaps; it does not wipe what a person already typed.
            if (ai && form) {
                const merged = { ...form };
                for (const k of Object.keys(next)) if (next[k] && !form[k]) merged[k] = next[k];
                setForm(merged);
            } else {
                setForm({ ...next, notes: form?.notes || '' });
            }
            setMissing(r.missing || []);
            setReadBy(ai ? 'ai' : 'paste');
        } catch (err) {
            setError(err.message || 'Could not read that.');
        } finally { setBusy(''); }
    };

    const save = async (e) => {
        e.preventDefault();
        if (!target) { setError('Pick the search to file this under.'); return; }
        setError(''); setBusy('save');
        try {
            const r = await apiClient.post(`/api/site-searches/${target}/paste`, {
                fields: form, notes: form.notes, source_url: url.trim() || null,
                source: detected || source, clip_id: clipId || undefined,
            });
            setSaved(r);
            if (onDone) onDone(r);
        } catch (err) {
            setError(err.message || 'Could not save.');
        } finally { setBusy(''); }
    };

    const field = 'mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm';
    const label = 'text-xs font-semibold text-gray-600';

    if (saved) {
        const z = saved.zoning || {};
        return (
            <div className="space-y-3 text-sm">
                <p className="rounded bg-green-50 px-3 py-2 text-green-800">{saved.message}</p>
                {z.available && (
                    <p className="text-xs text-gray-600">
                        Zoning: {z.error || z.message
                            || (z.zoned ? 'district found on the town map.' : z.queued ? 'no online map — queued for the office zoning check.' : 'nothing to look up.')}
                    </p>
                )}
                {!z.available && <p className="text-xs text-gray-500">Zoning district left blank — set it on the property row.</p>}
                <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => { setSaved(null); setForm(null); setUrl(''); setTitle(''); setText(''); setReadBy(null); }}
                            className="rounded border border-gray-300 px-3 py-1.5 text-xs">Add another</button>
                    {onCancel && <button type="button" onClick={onCancel} className="rounded bg-gray-800 px-3 py-1.5 text-xs font-semibold text-white">Close</button>}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

            <div className="space-y-2">
                <div>
                    <label className={label}>Listing URL</label>
                    <input className={field} value={url} onChange={(e) => setUrl(e.target.value)}
                           placeholder="https://www.loopnet.com/Listing/…" />
                    {detected && (
                        <p className="mt-1 text-[11px] text-gray-500">
                            Source: <b>{SOURCE_LABEL[detected]}</b>
                            {isBlocked(url) && ' — the portal does not open this site; paste the page text below so it can be read.'}
                        </p>
                    )}
                </div>
                <div>
                    <label className={label}>Page text</label>
                    <textarea rows={form ? 3 : 8} className={`${field} font-mono text-xs`} value={text}
                              onChange={(e) => setText(e.target.value.slice(0, status?.max_text || 60000))}
                              placeholder="On the listing page press Ctrl+A then Ctrl+C, and paste here (Ctrl+V)." />
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <button type="button" disabled={!!busy || (!url.trim() && !text.trim())} onClick={() => read(false)}
                            className="rounded bg-gray-800 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
                        {busy === 'parse' ? 'Reading…' : form ? 'Read again' : 'Read the listing'}
                    </button>
                    {status?.ai_available && (
                        <button type="button" disabled={!!busy || text.trim().length < 40} onClick={() => read(true)}
                                title="Ask Claude to read the pasted text. You still check every field before saving."
                                className="rounded border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 disabled:opacity-50">
                            {busy === 'ai' ? 'AI reading…' : 'AI extract'}
                        </button>
                    )}
                    {!form && (
                        <button type="button" onClick={() => { setForm({ ...EMPTY }); setReadBy('hand'); }}
                                className="text-xs text-indigo-700 hover:underline">or type it in by hand</button>
                    )}
                </div>
            </div>

            {form && (
                <form onSubmit={save} className="space-y-3 border-t pt-3">
                    <p className="text-[11px] text-gray-500">
                        {readBy === 'ai' ? 'Read by the AI from your paste' : readBy === 'paste' ? 'Read from your paste' : 'Typed by hand'}
                        {' '}— check every field; nothing is saved until you press Save.
                        {!!missing.length && readBy !== 'hand' && <> Not found: {missing.join(', ').replace(/_/g, ' ')}.</>}
                    </p>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {FIELD_LABELS.map(([k, l, ph]) => (
                            <div key={k} className={k === 'address' ? 'sm:col-span-2' : ''}>
                                <label className={label}>{l}</label>
                                <input className={field} value={form[k]} onChange={set(k)} placeholder={ph}
                                       required={k === 'address'} />
                            </div>
                        ))}
                        <div>
                            <label className={label}>Lease or sale</label>
                            <select className={field} value={form.listing_type} onChange={set('listing_type')}>
                                <option value="">—</option>
                                <option value="lease">For lease</option>
                                <option value="sale">For sale</option>
                                <option value="lease or sale">Lease or sale</option>
                            </select>
                        </div>
                        <div>
                            <label className={label}>Source</label>
                            {detected
                                ? <p className="mt-2 text-sm text-gray-700">{SOURCE_LABEL[detected]} <span className="text-xs text-gray-400">(from the URL)</span></p>
                                : (
                                    <select className={field} value={source} onChange={(e) => setSource(e.target.value)}>
                                        {Object.keys(SOURCE_LABEL).map((k) => <option key={k} value={k}>{SOURCE_LABEL[k]}</option>)}
                                    </select>
                                )}
                        </div>
                        <div className="sm:col-span-2">
                            <label className={label}>Notes</label>
                            <input className={field} value={form.notes} onChange={set('notes')} />
                            <p className="mt-1 text-[11px] text-gray-500">Lease/sale, the type and the zoning the listing states are added to the notes.
                                The zoning district itself comes from the town's map, not from the listing.</p>
                        </div>
                        {Array.isArray(searches) && (
                            <div className="sm:col-span-2">
                                <label className={label}>File under search</label>
                                <select required className={field} value={target} onChange={(e) => setTarget(e.target.value)}>
                                    <option value="">Pick a search…</option>
                                    {searches.map((s) => (
                                        <option key={s.search_id} value={s.search_id}>{s.client_name} — {s.title}{s.status !== 'active' ? ` (${s.status})` : ''}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                    <div className="flex justify-end gap-2">
                        {onCancel && <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-4 py-2 text-sm">Cancel</button>}
                        <button type="submit" disabled={!!busy} className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                            {busy === 'save' ? 'Saving…' : 'Save to search'}
                        </button>
                    </div>
                </form>
            )}

            {!form && <SendToBrecBookmarklet status={status} apiClient={apiClient} />}
        </div>
    );
};

export default AddListingPaste;
